import React, { useCallback, useEffect, useState } from 'react';

import { Route, Routes, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { ConfigProvider } from 'antd';

import './App.css';
import AddCode from './Components/Code/Add.js';
import CodeFeed from './Components/Code/Feed';
import LoggedInRoute from './Components/Permissions/LoggedInRoute.js';
import Login from './Components/User/Login';
import Logout from './Components/User/Logout';
import Navbar from './Components/UI/Navbar.js';
import Register from './Components/User/Register';
import ResetPassword from './Components/User/ResetUserPassword';
import ViewCode from './Components/Code/View';
import NotificationList from './Components/User/NotificationList';

import { getUserNewNotifications } from './Helpers/User.js';

const antdTheme = {
  token: {
    colorBgContainer: 'var(--panel)',
    colorBgLayout: 'var(--bg)',
    borderRadius: 6,
    fontFamily: "var(--sans)",
    colorText: 'var(--fg)',
    colorPrimary: '#7ee787',
    colorBorder: 'var(--line)',
  },
  components: {
    Layout: {
      colorBgHeader: 'transparent',
      colorBgBody: 'transparent',
      colorBgTrigger: 'transparent',
    },
  },
};

function App() {
  const [userData, setUserData] = useState({});
  const [newNotificationsCount, setNewNotificationsCount] = useState(null);
  const [shouldRender, setShouldRender] = useState(false);
  const [theme, setTheme] = useState('dark');

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  function onSetUserSession(id, role) {
    setUserData({ id: id, role: role });
  }

  const setUserSession = useCallback(async () => {
    const sessionDataResponse = await fetch('/api/session-data')
      .then(response => response.json())
      .then(data => data);
    if (sessionDataResponse.success === 1) {
      const userSession = sessionDataResponse.session;
      userSession.user_id ?
        window.sessionStorage.setItem('userId', userSession.user_id) :
        window.sessionStorage.removeItem('userId');
      userSession.user_role ?
        window.sessionStorage.setItem('userRole', userSession.user_role) :
        window.sessionStorage.removeItem('userRole');
      onSetUserSession(userSession.user_id, userSession.user_role);
    }
  }, []);

  function getUserData() {
    return {
      id: window.sessionStorage.getItem('userId'),
      role: window.sessionStorage.getItem('userRole')
    };
  }

  useEffect(() => {
    setUserSession().then(() => setShouldRender(true));
  }, [setUserSession]);

  useEffect(() => {
    getUserNewNotifications().then(data => {
      setNewNotificationsCount(data.notifications_count);
    });
    const timer = setInterval(() => {
      getUserNewNotifications().then(data => {
        setNewNotificationsCount(data.notifications_count);
      });
    }, 30000);
    return () => clearInterval(timer);
  }, [userData]);

  if (!shouldRender) return null;

  return (
    <ConfigProvider theme={antdTheme}>
      <div className="cs-shell">
        <Navbar
          newNotificationsCount={newNotificationsCount}
          userData={userData}
          theme={theme}
          onSetTheme={setTheme}
        />

        <main className="cs-main">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route
              path="/login"
              element={<Login onLoginSuccess={setUserSession} />}
            />
            <Route path="/register" element={<Register />} />
            <Route path="/code/add" element={
              <LoggedInRoute userData={getUserData()}>
                <AddCode />
              </LoggedInRoute>
            } />
            <Route path="/user/admin-reset-password" element={
              <LoggedInRoute userData={getUserData()}>
                <ResetPassword userData={getUserData()} />
              </LoggedInRoute>
            } />
            <Route path="/code/:id/view" element={
              <LoggedInRoute userData={getUserData()}>
                <ViewCode userData={getUserData()} />
              </LoggedInRoute>
            } />
            <Route
              path="/code/feed/:userId"
              element={<CodeFeed userData={getUserData()} />}
            />
            <Route
              path="/code/feed"
              element={<CodeFeed userData={getUserData()} />}
            />
            <Route path="/user/:userId/notifications" element={
              <NotificationList
                updateNewNotificationsCount={setNewNotificationsCount}
              />
            } />
            <Route path="/logout" element={
              <LoggedInRoute userData={userData}>
                <Logout onLogoutSuccess={setUserSession} />
              </LoggedInRoute>
            } />
          </Routes>
        </main>

        <StatusBar />
      </div>
    </ConfigProvider>
  );
}

function StatusBar() {
  const { t } = useTranslation();
  const location = useLocation();

  function screenLabel() {
    if (location.pathname.endsWith('/view')) return 'review';
    if (location.pathname === '/code/add') return 'compose';
    if (location.pathname.startsWith('/code/feed')) return 'feed';
    return location.pathname.slice(1) || 'home';
  }

  return (
    <footer className="cs-statusbar">
      <span><span className="accent">●</span> {t('connected')}</span>
      <span>{t(screenLabel())}</span>
      <span className="cs-statusbar__hint">utf-8</span>
      <span className="cs-statusbar__spacer" />
      <span className="cs-statusbar__hint">
        G F feed · G A add · / search
      </span>
      <span>v0.4.2</span>
    </footer>
  );
}

function Home() {
  const { t } = useTranslation();
  return (
    <div
      style={{
        display: 'grid',
        placeItems: 'center',
        height: '100%',
        color: 'var(--fg-2)',
        fontFamily: 'var(--mono)',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>🐾</div>
        <h2
          style={{
            margin: 0,
            color: 'var(--fg)',
            fontFamily: 'var(--mono)',
            fontWeight: 600,
            letterSpacing: '-0.01em',
          }}
        >
          {t('Home')}
        </h2>
        <p style={{ color: 'var(--fg-3)', marginTop: 8, fontSize: 12 }}>
          {t('welcome to codesniffer.')}
        </p>
      </div>
    </div>
  );
}

function About() {
  const { t } = useTranslation();
  return (
    <div
      style={{
        display: 'grid',
        placeItems: 'center',
        height: '100%',
        color: 'var(--fg-2)',
        fontFamily: 'var(--mono)',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <h1 style={{ color: 'var(--fg)', fontFamily: 'var(--mono)' }}>
          {t('About')}
        </h1>
      </div>
    </div>
  );
}

export { App };
export default App;
